import type { UnitKind } from '../../api/objects-write'
import type { MessageKey } from '../../i18n'

/**
 * Which questions make sense about which kind of space.
 *
 * Asking a room how many bedrooms it has is asking it how many of itself it
 * contains, and asking a place how many bathrooms it has is asking about a
 * fitting that belongs to the flat. Both were on the form, and both are the kind
 * of thing that makes somebody stop trusting a screen: if it does not know what
 * a room is, what else does it not know.
 *
 * So the questions are declared per kind, once, and every form reads them from
 * here rather than each deciding for itself and drifting.
 *
 * The shape of the tree is the reason the lists differ:
 *
 * - A flat contains rooms. How many rooms and how many bathrooms are facts about
 *   the flat.
 * - A room contains beds. How many people sleep in it, and whether it is theirs
 *   alone, are facts about the room. How many bedrooms it has is not a question.
 * - A place is one bed. Everything about it beyond a name belongs to the room it
 *   is in.
 */

/** One number a person may be asked about a space. */
export interface NumberField {
  readonly id: 'bedrooms' | 'beds' | 'bathrooms' | 'capacity' | 'area_sqm'
  readonly label: MessageKey
  readonly hint?: MessageKey
  /** Below this it is not a smaller number, it is a different claim. */
  readonly min: number
  readonly step?: string
}

const AREA: NumberField = {
  id: 'area_sqm',
  label: 'build.areaSqm',
  hint: 'build.areaHint',
  min: 0,
  // Roman flats are advertised at 62.5 m². Whole metres would round somebody's
  // home down in our own record.
  step: '0.5',
}

const CAPACITY: NumberField = {
  id: 'capacity',
  label: 'build.capacity',
  hint: 'build.capacityHint',
  // Zero is not a smaller capacity, it is a space nobody may live in.
  min: 1,
}

const NUMBERS: Record<UnitKind, readonly NumberField[]> = {
  apartment: [
    { id: 'bedrooms', label: 'build.bedrooms', min: 0 },
    { id: 'bathrooms', label: 'build.bathrooms', min: 0 },
    { id: 'beds', label: 'build.beds', min: 0 },
    CAPACITY,
    AREA,
  ],
  room: [
    // Beds, not bedrooms: a room is one, and how many people sleep in it is the
    // question somebody actually has.
    { id: 'beds', label: 'build.beds', min: 0 },
    CAPACITY,
    AREA,
  ],
  // A place is one bed for one person. There is nothing to count.
  bed: [],
}

export function numbersFor(kind: UnitKind): readonly NumberField[] {
  return NUMBERS[kind]
}

/** Whether "is it theirs alone" is a question about this kind of space. */
export function asksPrivacy(kind: UnitKind): boolean {
  // Only a room can be one or the other. A flat is by definition, and a place is
  // by definition not -- that is what makes it a place rather than a room.
  return kind === 'room'
}

/** Whether furniture is a question about this kind of space. */
export function asksFurniture(kind: UnitKind): boolean {
  return kind !== 'bed'
}
